import fp from "lodash/fp"
import { DateTime } from "luxon"

import type {
  PlotView,
  PlotReqConfig,
  SingleStatViewProperties,
  FieldConversion,
} from "types/dashboard.types"
import type {
  DeviceTelemetryRes,
  EntityTelemetry,
  PowerlineTelemetryRes,
} from "types/telemetries.types"
import type { FieldQualityInfo, FieldQualityMap } from "types/quality.types"

import { isQualityLevel } from "helpers/utils/quality"
import { formatTelemetryPlotData } from "helpers/utils/plots"

type GetFill = (
  view: PlotView,
  config: PlotReqConfig,
  entityIds?: number[],
  geohashes?: string[],
) => string[]
export const getFill: GetFill = (view, config, entityIds, geohashes) => {
  const minimalFill = ["_field"]
  const groupFill = fp.getOr(minimalFill, "group", config)
  const fill = fp.getOr(groupFill, "properties.fill", view)
  const aggregates = fp.getOr([], "aggregate", config)
  const extra = [
    // if we have multiple aggregates, we need the result in the fill
    ...(aggregates.length > 1 ? ["result"] : []),
    // if we have multiple entities, we need the entity id in the fill to separate lines
    ...(entityIds && entityIds.length > 1 ? ["id"] : []),
    // if we have multiple geohashes, we need the geohash in the fill to separate lines
    ...(geohashes && geohashes.length > 1 ? ["geohash"] : []),
  ]
  return fp.uniq(fp.concat(fill, extra))
}

export const formatTelemetryData = (
  data: EntityTelemetry[] | undefined,
  fieldConversions?: { [fieldName: string]: FieldConversion },
) => {
  const result = formatTelemetryPlotData(data, fieldConversions)
  return result
}

export const removeOldForecastData = (
  data: DeviceTelemetryRes | PowerlineTelemetryRes | undefined,
  forecastFields: string[] | undefined,
  thresholdDate: DateTime | null,
  isRawData: boolean = false,
): DeviceTelemetryRes | PowerlineTelemetryRes | undefined => {
  if (fp.isEmpty(forecastFields) || !thresholdDate) {
    return data
  }

  // Goal: cut the forecast so it starts right where the measured series ends,
  // without leaving a gap or an overlapping point between the two lines.
  //
  // Aggregated data: points are grouped into time windows whose size grows with
  // the requested range. Measured and forecast share the same window grid, so
  // the window that contains "now" produces a point in BOTH series at the same
  // timestamp — this is the overlap. We cut the forecast at the last measured
  // timestamp to drop that shared point. This works at any window size, whereas
  // a fixed offset (e.g. "5 minutes") could not adapt to it.
  //
  // Raw data: no windows, so the forecast already starts just after the
  // threshold with no overlap. We skip the last-measured cut here, since it
  // could otherwise trim away a legitimately short forecast.
  const forecastFieldSet = new Set(forecastFields)
  const measuredTimes = isRawData
    ? []
    : (data ?? [])
        .filter((d) => d._field != null && !forecastFieldSet.has(d._field))
        .map((d) => DateTime.fromISO(d._time))
  const lastMeasured = measuredTimes.length ? DateTime.max(...measuredTimes) : null
  const cutoff =
    lastMeasured && lastMeasured > thresholdDate ? lastMeasured : thresholdDate

  const filterOldData = (fieldData: DeviceTelemetryRes | PowerlineTelemetryRes) =>
    fieldData?.filter((d) => DateTime.fromISO(d._time) > cutoff)

  return fp.flow(
    fp.defaultTo([]),
    fp.groupBy("_field"),
    fp.toPairs,
    fp.map(([field, fieldData]: [string, DeviceTelemetryRes | PowerlineTelemetryRes]) =>
      forecastFields?.includes(field)
        ? [field, filterOldData(fieldData)]
        : [field, fieldData],
    ),
    fp.fromPairs,
    fp.flatMap((values) => values),
  )(data)
}

export const removeOldShortTermAmpacityData = (
  data: DeviceTelemetryRes | PowerlineTelemetryRes | undefined,
  nameKey: string,
): DeviceTelemetryRes | PowerlineTelemetryRes | undefined => {
  const isShortTermAmpacity = nameKey === "ACTIVE_SHORT_TERM_AMPACITY"

  if (!isShortTermAmpacity) {
    return data
  }

  const now = DateTime.now()
  const fifteenMinutesAgo = now.minus({ minutes: 15 })

  return data?.filter((d) => DateTime.fromISO(d._time) > fifteenMinutesAgo)
}

export const filterTelemetryDataByTime = (
  data: DeviceTelemetryRes | PowerlineTelemetryRes | undefined,
  forecastFields: string[] | undefined,
  nameKey: string,
  thresholdDate: DateTime | null,
  isRawData: boolean = false,
): DeviceTelemetryRes | PowerlineTelemetryRes | undefined => {
  const withoutOldForecast = removeOldForecastData(
    data,
    forecastFields,
    thresholdDate,
    isRawData,
  )
  const withoutOldShortTerm = removeOldShortTermAmpacityData(withoutOldForecast, nameKey)
  return withoutOldShortTerm
}

export const divideChartAndTooltipData = (
  data: EntityTelemetry[],
  tooltipOnlyFields: string[] | undefined,
) => {
  if (fp.isEmpty(tooltipOnlyFields)) {
    return [data, []]
  }
  const tooltipOnlyFieldsSet = new Set(tooltipOnlyFields)
  return fp.partition((d) => d._field && !tooltipOnlyFieldsSet.has(d._field), data)
}

export const applyDataTransform = (
  formattedData: EntityTelemetry[],
  view: PlotView,
): EntityTelemetry[] => {
  if (view.type !== "single-stat") {
    return formattedData
  }

  const properties = view.properties as SingleStatViewProperties
  const { dataTransform, dataThreshold } = properties

  switch (dataTransform?.type) {
    case "percentage":
      if (!fp.isUndefined(dataThreshold)) {
        return calculatePercentageAboveBelowThreshold(
          formattedData,
          dataThreshold,
          dataTransform.condition,
        )
      }
      break
    default:
      return formattedData
  }
  return formattedData
}

export const calculatePercentageAboveBelowThreshold = (
  data: EntityTelemetry[],
  threshold: number,
  condition: "above" | "below",
): EntityTelemetry[] => {
  if (!data || data.length === 0) {
    return []
  }

  const totalPoints = data.length
  const matchingPoints = data.filter((d) => {
    if (typeof d._value !== "number") return false
    return condition === "above" ? d._value > threshold : d._value < threshold
  }).length

  const percentage = totalPoints > 0 ? (matchingPoints / totalPoints) * 100 : 0

  const lastRecord = data[data.length - 1]

  return [
    {
      ...lastRecord,
      _value: percentage,
    },
  ]
}

const QUALITY_SUFFIX_QUALITY_INDEX = ".metadata.quality_index"
const QUALITY_SUFFIX_QUALITY = ".metadata.quality"
const QUALITY_SUFFIX_REASON = ".metadata.reason"

type QualityFieldParsed = {
  baseField: string
  key: "quality" | "qualityIndex" | "reason"
}

const parseQualityField = (field: string): QualityFieldParsed | null => {
  if (field.endsWith(QUALITY_SUFFIX_QUALITY_INDEX))
    return {
      baseField: field.slice(0, -QUALITY_SUFFIX_QUALITY_INDEX.length),
      key: "qualityIndex",
    }
  if (field.endsWith(QUALITY_SUFFIX_QUALITY))
    return { baseField: field.slice(0, -QUALITY_SUFFIX_QUALITY.length), key: "quality" }
  if (field.endsWith(QUALITY_SUFFIX_REASON))
    return { baseField: field.slice(0, -QUALITY_SUFFIX_REASON.length), key: "reason" }
  return null
}

export const extractQualityMetadata = (
  data: EntityTelemetry[],
  isRawData: boolean,
): { filteredData: EntityTelemetry[]; fieldQualityMap: FieldQualityMap | undefined } => {
  if (data.length === 0) {
    return { filteredData: data, fieldQualityMap: undefined }
  }

  const filteredData: EntityTelemetry[] = []
  const fieldQualityMap: FieldQualityMap = {}

  for (const record of data) {
    const { _field, _time, _value } = record

    if (!_field) {
      filteredData.push(record)
      continue
    }

    const parsed = parseQualityField(_field)

    if (!parsed) {
      filteredData.push(record)
      continue
    }

    // Quality metadata fields must never be plotted as chart lines; they only
    // feed per-point quality into the tooltip via fieldQualityMap. Strip them
    // unconditionally: aggregated responses don't include them, but when exiting
    // zoom out of a raw-data range `isRawData` flips to false a frame before the
    // aggregated fetch resolves, so the previous raw response (which carries
    // these fields) lingers in `data`. Without stripping here regardless of the
    // flag, those quality lines leak onto the plot until the new data arrives.
    // The map, however, is only meaningful for raw data, so skip building it.
    if (!isRawData) continue

    if (!_time) continue

    const { baseField, key } = parsed
    const timeMillis = DateTime.fromISO(_time).toMillis()

    fieldQualityMap[timeMillis] ??= {}
    const existing = fieldQualityMap[timeMillis][baseField] ?? {
      quality: undefined,
      qualityIndex: undefined,
      reason: undefined,
    }

    const update: Partial<FieldQualityInfo> =
      key === "qualityIndex"
        ? { qualityIndex: typeof _value === "number" ? _value : undefined }
        : key === "quality"
          ? { quality: isQualityLevel(_value) ? _value : undefined }
          : { reason: String(_value) }

    fieldQualityMap[timeMillis][baseField] = { ...existing, ...update }
  }

  if (Object.keys(fieldQualityMap).length === 0) {
    return { filteredData, fieldQualityMap: undefined }
  }

  return { filteredData, fieldQualityMap }
}
